import { findFieldById, findSectionById, normalizeSeriesName } from '../base-utils';
import { BasicInfoCommonFieldIds, TAssembledApiData } from '../common.models';
import {
    FORESITE_XD_MONITOR_CONFIGURATION_CONSTANTS,
    ForeSiteRadarSensorFieldIds,
    ForeSITEXD_CONSTANTS,
    ForeSiteXDalarmsFieldIds,
    ForeSITEXDDataDeliveryFieldIds,
    INSTALLATION_DEFAULT_CONSTANTS,
} from './base-config-models';
import { ChannelInstallationFieldIds, ElevationInstallationFieldIds } from './installation-types/config-models';
import { FlumeInstallationFieldIds, PipeInstallationFieldIds, WeirInstallationFieldIds } from '../common.models';
import { DefinedInstallationType, INSTALLATION_TYPE } from 'app/shared/models/location-details';
import { DeviceTypeCode } from 'app/shared/enums/device-type-code';
import { AutocompleteField, FieldType, SelectField, TextField } from '../../models/field-models';
import { RainGaugeLocation } from '../../../models/location-card.model';
import { ForeSiteXDLocationPayload, ForeSiteXDConfigurationPayload } from './base-api-models';
import { LocationCommunicationType } from '../../../location-card.constants';
import { ForesiteXDLocationCardState } from './models';
import { LocationStatus } from 'app/shared/models/locations';

export function gatherForeSiteXDBasicInfoPayload(
    state: ForesiteXDLocationCardState,
): ForeSiteXDLocationPayload[] | null {
    const installationType = SaveInstallationPayload(state);
    if (!installationType) {
        return null;
    }

    let mp1Info: Partial<ForeSiteXDLocationPayload> = { ...ForeSITEXD_CONSTANTS };

    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    mp1Info = { ...mp1Info, ...installationType };

    return [mp1Info as ForeSiteXDLocationPayload];
}

export const SaveBasicInfoPayload: { [key: string]: (state: ForesiteXDLocationCardState) => TAssembledApiData } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName)!.value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    communicationType: () => LocationCommunicationType.External,
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description)!.value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate)!.value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude)!.value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude)!.value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress)!.value,
    manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth)!.value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series)!.value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status)!.value,
    maintenanceInterval: (state) => {
        const status = findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value;
        return status === LocationStatus.Maintenance
            ? findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null
            : null;
    },
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)!.value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(
            state.formState,
            BasicInfoCommonFieldIds.CustomPropertiesSection,
        );
        const customProperties: { key: string; value: string }[] = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`
            ] as AutocompleteField<string, string>;
            const valueField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`
            ] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveInstallationPayload = (state: ForesiteXDLocationCardState) => {
    const installationType = findFieldById(
        state.formState,
        BasicInfoCommonFieldIds.InstallationType,
        FieldType.Select,
    )?.value;

    if (typeof installationType !== 'string' || !installationType) {
        console.error('Installation type not found');
        return null;
    }

    const [type, shape] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.PIPE) {
        return {
            pipe: {
                height: findFieldById(state.formState, PipeInstallationFieldIds.Height)!.value,
                width: findFieldById(state.formState, PipeInstallationFieldIds.Width)!.value,
                lookupDesc: type,
                hc: findFieldById(state.formState, PipeInstallationFieldIds.Hc)!.value,
                roughness: findFieldById(state.formState, PipeInstallationFieldIds.Roughness)!.value,
                effectiveRoughness: findFieldById(state.formState, PipeInstallationFieldIds.EffectiveRoughness)!.value,
                slope: findFieldById(state.formState, PipeInstallationFieldIds.Slope)!.value,
                frictionFactor: findFieldById(state.formState, PipeInstallationFieldIds.FrictionFactor)!.value,
                nominalDiameter: findFieldById(state.formState, PipeInstallationFieldIds.NominalDiameter)!.value,
                startDate: findFieldById(state.formState, PipeInstallationFieldIds.StartDate)!.value,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Pipe,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Pipe,
            installationtypeid: DefinedInstallationType.Pipe,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.WEIR) {
        return {
            weir: {
                range: findFieldById(state.formState, WeirInstallationFieldIds.Range)!.value,
                capacity: findFieldById(state.formState, WeirInstallationFieldIds.Capacity)!.value,
                height: findFieldById(state.formState, WeirInstallationFieldIds.Height)!.value,
                coefficient: findFieldById(state.formState, WeirInstallationFieldIds.Coefficient)!.value,
                length: findFieldById(state.formState, WeirInstallationFieldIds.Length)!.value,
                breadth: findFieldById(state.formState, WeirInstallationFieldIds.Breadth)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Weir,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
                depthSensor: 100,
                velocitySensor: 100,
            },
            installationID: DefinedInstallationType.Weir,
            installationtypeid: DefinedInstallationType.Weir,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.FLUME) {
        return {
            flume: {
                range: findFieldById(state.formState, FlumeInstallationFieldIds.Range)!.value,
                capacity: findFieldById(state.formState, FlumeInstallationFieldIds.Capacity)!.value,
                width: findFieldById(state.formState, FlumeInstallationFieldIds.Width)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Flume,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Flume,
            installationtypeid: DefinedInstallationType.Flume,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.CHANNEL) {
        const range: unknown = findFieldById(
            state.formState,
            ChannelInstallationFieldIds.Range,
            FieldType.Number,
        )?.value;
        if (
            !shape ||
            !Number.isInteger(Number(shape)) ||
            (typeof range !== 'number' && typeof range !== 'string') ||
            (typeof range === 'string' && range.trim() === '') ||
            !Number.isFinite(Number(range))
        ) {
            return null;
        }

        return {
            channel: {
                range: Number(range),
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Channel,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Channel,
            installationtypeid: DefinedInstallationType.Channel,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.ELEVATION) {
        return {
            elevationInstallation: {
                datum: findFieldById(state.formState, ElevationInstallationFieldIds.Datum)!.value,
                physicalOffset: findFieldById(state.formState, ElevationInstallationFieldIds.PhysicalOffset)!.value,
                referencePoint: findFieldById(state.formState, ElevationInstallationFieldIds.ReferencePoint)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Elevation,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Elevation,
            installationtypeid: DefinedInstallationType.Elevation,
            installationShapeType: shape,
        };
    }

    console.error('Installation type not supported');
    return null;
};

export function gatherForeSiteXDconfigurationInfo(state: ForesiteXDLocationCardState): ForeSiteXDConfigurationPayload {
    const radarSensor: { [key: string]: (state: ForesiteXDLocationCardState) => TAssembledApiData } = {
        name: () => 'Radar',
        maxMeasurementRange: (state) =>
            findFieldById(state.formState, ForeSiteRadarSensorFieldIds.MaxMeasurementRange)!.value,
        physicalOffset: (state) => findFieldById(state.formState, ForeSiteRadarSensorFieldIds.PhysicalOffset)!.value,
        type: () => DeviceTypeCode.ForeSiteRadar,
    };

    const result: { [key: string]: (state: ForesiteXDLocationCardState) => TAssembledApiData } = {
        manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth)!.value,
        samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)!.value,
        normalIntervalHours: (state) =>
            findFieldById(state.formState, ForeSITEXDDataDeliveryFieldIds.NormalIntervalHours)!.value,
        fastModeEnabled: (state) =>
            findFieldById(state.formState, ForeSITEXDDataDeliveryFieldIds.FastModeEnabled)!.value,
        lowLevelEnable: (state) => findFieldById(state.formState, ForeSiteXDalarmsFieldIds.LowLevelEnable)!.value,
        lowLevelThreshold: (state) => findFieldById(state.formState, ForeSiteXDalarmsFieldIds.LowLevelThreshold)!.value,
        highLevelEnable: (state) => findFieldById(state.formState, ForeSiteXDalarmsFieldIds.HighLevelEnable)!.value,
        highLevelThreshold: (state) =>
            findFieldById(state.formState, ForeSiteXDalarmsFieldIds.HighLevelThreshold)!.value,
        highHighEnable: (state) => findFieldById(state.formState, ForeSiteXDalarmsFieldIds.HighHighEnable)!.value,
        highHighThreshold: (state) => findFieldById(state.formState, ForeSiteXDalarmsFieldIds.HighHighThreshold)!.value,
        batteryLowEnable: (state) => findFieldById(state.formState, ForeSiteXDalarmsFieldIds.BatteryLowEnable)!.value,
        batteryLowThreshold: (state) =>
            findFieldById(state.formState, ForeSiteXDalarmsFieldIds.BatteryLowThreshold)!.value,
        serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
        useDaylightSavingTime: (state) => state.monitorConfiguration?.usedaylightsaving ?? false,
        storeTemperature: (state) => state.monitorConfiguration?.storetemperature ?? false,
        storeBattery: (state) => state.monitorConfiguration?.storebattery ?? 1,
        connectionType: () => LocationCommunicationType.External,
        syncTime: (state) => state.monitorConfiguration?.synctime ?? false,
        fastRate: (state) => state.monitorConfiguration?.fastrate ?? ForeSITEXD_CONSTANTS.fastrate,
    };

    const radarInfo = Object.keys(radarSensor).reduce((acc, key) => {
        acc[key] = radarSensor[key](state);
        return acc;
    }, {});

    return Object.keys(result).reduce(
        (acc, key) => {
            acc[key] = result[key](state);
            return acc;
        },
        { ...FORESITE_XD_MONITOR_CONFIGURATION_CONSTANTS, devices: [radarInfo] },
    ) as ForeSiteXDConfigurationPayload;
}
